/**
 * Video Utilities
 *
 * Helper functions for video URL handling based on user paid status.
 * Supports both subscription users and credit-pack purchasers.
 */

import type { Asset } from '../services/assets';
import type { User } from '../types/api';

/**
 * Determine which video URL to use based on user's paid status.
 * Free users see watermarked version, paid users see original.
 *
 * "Paid" means either:
 *   - Active subscription (subscription_status === 'active' | 'canceling')
 *   - Has purchased a credit pack (has_purchased_credits === true)
 *
 * @param asset - The asset containing video URLs
 * @param user - The user object with subscription/purchase information (can be null for anonymous users)
 * @returns The appropriate video URL to display
 */
export function getVideoUrl(asset: Asset, user: User | null): string | undefined {
  if (hasPaidAccess(user)) {
    // Paid users see original unwatermarked version
    return asset.video_url;
  } else {
    // Free users see watermarked version (fallback to unwatermarked if not available)
    return asset.watermarked_video_url || asset.video_url;
  }
}

/**
 * Check if a user has an active paid subscription
 *
 * @param user - The user object with subscription information
 * @returns True if user has active paid subscription
 */
export function hasActivePaidSubscription(user: User | null): boolean {
  return !!(user?.subscription_status &&
    (user.subscription_status === 'active' || user.subscription_status === 'canceling'));
}

/**
 * Check if a user has any form of paid access (subscription OR credit pack purchase).
 * Used for watermark gating and other paid-user feature checks.
 *
 * @param user - The user object
 * @returns True if user has paid access via subscription or credit pack purchase
 */
export function hasPaidAccess(user: User | null): boolean {
  if (!user) return false;
  return hasActivePaidSubscription(user) || !!user.has_purchased_credits;
}
